import {
    Checkbox,
    Paper,
    Stack,
    Typography,
    type CheckboxProps,
    type PaperProps,
    type StackProps,
} from '@mui/material'
import {
    DataGridPro,
    GridToolbarColumnsButton,
    type BaseCheckboxPropsOverrides,
    type DataGridProProps,
    type GridValidRowModel,
} from '@mui/x-data-grid-pro'
import { type GridBaseSlots } from '@mui/x-data-grid/models/gridSlotsComponent'
import { forwardRef, type ReactNode } from 'react'
import { CenteredBox } from 'shared/components/CenteredBox'
import { getPxAsRem } from 'shared/utils'

const getNoRowsOverlay = () => (
    <CenteredBox>
        <Typography>No results found</Typography>
    </CenteredBox>
)

const getToolbarLayout = () => (
    <Stack
        direction="row"
        gap={getPxAsRem(8)}
        paddingBlockStart={getPxAsRem(16)}
        paddingInline={getPxAsRem(16)}
    >
        <GridToolbarColumnsButton slotProps={{ button: { variant: 'text' } }} />
    </Stack>
)

export interface SearchResultsWithPaginationTableProps<
    TRowModel extends GridValidRowModel,
> extends DataGridProProps<TRowModel> {
    /** Props to pass to the table's containing parent element. */
    containerProps?: StackProps & PaperProps
    /** The layout to render at the top of the table, above the table toolbar. */
    HeaderLayout?: ReactNode
    /** A custom function to run when checking the "Select All" checkbox. Providing this or `onUnselectAllRows` will override the default "Select All" behavior. */
    onSelectAllRows?: () => void
    /** A custom function to run when unchecking the "Select All" checkbox. Providing this or `onUnselectAllRows` will override the default "Select All" behavior. */
    onUnselectAllRows?: () => void
    /** Total number of rows for pagination */
    rowCount?: number
    /** Pagination model for MUI DataGrid */
    paginationModel?: { page: number; pageSize: number }
    /** Callback when pagination model changes */
    onPaginationModelChange?: (model: {
        page: number
        pageSize: number
    }) => void
}

export function SearchResultsWithPaginationTable<
    TRowModel extends GridValidRowModel,
>({
    containerProps = {},
    HeaderLayout,
    onSelectAllRows,
    onUnselectAllRows,
    rowCount = 0,
    paginationModel,
    onPaginationModelChange,
    sx: dataGridProSx,
    slots: dataGridProSlots,
    ...dataGridProProps
}: SearchResultsWithPaginationTableProps<TRowModel>) {
    const { sx: containerSx, ...otherContainerProps } = containerProps

    let getBaseCheckbox: GridBaseSlots['baseCheckbox'] | undefined = undefined

    // If custom select/deselect all functionality is provided, replace the
    // default "select all" checkbox with one that implements those callbacks.
    if (onSelectAllRows || onUnselectAllRows) {
        getBaseCheckbox = forwardRef(
            (
                { ...params }: CheckboxProps & BaseCheckboxPropsOverrides,
                ref
            ) => {
                const handleChange: CheckboxProps['onChange'] = (
                    event,
                    checked
                ) => {
                    // The `aria-label` attribute is currently the only way to
                    // consistently differentiate a DataGrid's "select all"
                    // checkbox from those of individual rows.
                    switch (params.inputProps?.['aria-label']) {
                        case 'Select all rows':
                            if (onSelectAllRows) onSelectAllRows()
                            break
                        case 'Unselect all rows':
                            if (onUnselectAllRows) onUnselectAllRows()
                            break
                        case 'Select row':
                        case 'Unselect row':
                            if (params.onChange) params.onChange(event, checked)
                            break
                    }
                }

                return (
                    <Checkbox {...params} onChange={handleChange} ref={ref} />
                )
            }
        )
    }

    return (
        <Stack
            component={Paper}
            elevation={0}
            sx={{ flex: 1, overflow: 'hidden', ...containerSx }}
            {...otherContainerProps}
        >
            {HeaderLayout}
            <DataGridPro
                sx={{ border: 0, ...dataGridProSx }}
                checkboxSelection
                disableRowSelectionOnClick
                disableColumnMenu
                paginationMode="server"
                pagination
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
                slots={{
                    toolbar: getToolbarLayout,
                    noRowsOverlay: getNoRowsOverlay,
                    baseCheckbox: getBaseCheckbox,
                    ...dataGridProSlots,
                }}
                {...dataGridProProps}
            />
        </Stack>
    )
}
